/**
 * QR code rendering, web implementation.
 *
 * Encodes to a data URL with the `qrcode` package and renders it through React
 * Native's `Image`, so the same component sits inside a normal themed layout and
 * measures in density-independent pixels.
 *
 * The native counterpart (`qr-code.tsx`) is a documented placeholder; a device
 * build would swap in `react-native-qrcode-svg` without touching call sites.
 */

import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { View } from 'react-native';

import { Stack } from './layout';
import { Text } from './ui/text';
import type { QrCodeProps } from '../lib/qr-types';

/**
 * Renders a value as a QR code.
 *
 * @param props Value, size, and colours.
 * @returns The rendered code, or a placeholder while encoding.
 */
export function QrCode({
  value,
  size = 220,
  margin = 2,
  level = 'M',
  label,
  backgroundColor,
  foregroundColor,
}: QrCodeProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The encoder paints pixels, so its two colours cannot come from a class name
  // — but they must not follow the theme either. A QR code is a machine-read
  // symbol, and every reader in this product, including the merchant app's own
  // ZXing decoder, expects dark modules on a light field. Inverting it in dark
  // mode would produce a code that looks right and scans nowhere.
  const background = backgroundColor ?? '#FFFFFF';
  const foreground = foregroundColor ?? '#1C1917';

  useEffect(() => {
    let cancelled = false;

    QRCode.toDataURL(value, {
      errorCorrectionLevel: level,
      margin,
      width: size * 2, // Encoded at 2x so it stays crisp on a retina iPad.
      color: { dark: foreground, light: background },
    })
      .then((url) => {
        if (!cancelled) {
          setDataUrl(url);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not render QR code');
      });

    return () => {
      cancelled = true;
    };
  }, [value, size, margin, level, background, foreground]);

  if (error) {
    return (
      <Stack gap="xs" align="center">
        <Text className="text-destructive text-center text-xs font-medium">{error}</Text>
        <Text selectable className="text-subtle-foreground text-center font-mono text-[11px]">
          {value}
        </Text>
      </Stack>
    );
  }

  return (
    <View
      className="items-center justify-center overflow-hidden rounded-lg"
      // Painted behind the code so the frame matches the encoder's light colour.
      style={{ width: size, height: size, backgroundColor: background }}
      accessibilityRole="image"
      accessibilityLabel={label ? `QR code: ${label}` : 'QR code'}>
      {dataUrl ? (
        /*
         * A real `img`, not React Native Web's `Image`: that paints its source
         * as a CSS `background-image` and renders an `opacity: 0` copy for
         * assistive technology. Browsers leave backgrounds out of a print unless
         * the reader ticks "background graphics", so the sticker — whose entire
         * purpose is the code — printed blank. An `img` is content, and content
         * always prints.
         */
        <img
          src={dataUrl}
          alt={label ? `QR code: ${label}` : 'QR code'}
          width={size}
          height={size}
          style={{ display: 'block' }}
        />
      ) : null}
    </View>
  );
}
